<script setup lang="ts">
// TODO: migrate off deprecated package
import { useFlowControlStore } from '@components/flow-controller'
import { IconCheckupList, IconEdit, IconExclamationCircle } from '@tabler/icons-vue'
import { computed, watchEffect } from 'vue'

import BaseButton from '@tix/ds/components/atoms/BaseButton.vue'
import StatusMessage from '@tix/ds/components/atoms/StatusMessage.vue'
import type { Schema } from '@tix/openapi-sdk'

import { type SummaryGroup, type SummaryRow, groupConsecutiveReviewRows } from '@/helpers/summaryRows'

import { errorTracker } from '@/services/errorTracking'

import BaseCard from '@/components/BaseCard.vue'
import SelectionSummary from '@/components/bundles/selections/SelectionSummary.vue'

import { useFlags } from '@/composables/useFlags'
import { useFlowSelections } from '@/composables/useFlowSelections'

import ActionHeader from './ActionHeader.vue'

const emit = defineEmits<{
  'go-to-step': [resolverId: string, type?: string]
}>()

const actionType: Schema<'action_confirm'>['type'] = 'confirm'

const store = useFlowControlStore()
const flags = useFlags()
const { rows, subtotal } = useFlowSelections()

/** The same flag the page lays the sidebar out with; on the confirm step it replaces `review_summary`. */
const showSelections = computed(() => Boolean(flags.value.flows_progress_sidebar))

const action = computed<Schema<'action_prompt_confirm'> | null>(
  () => (store.action?.type === actionType && store.action) || null,
)

store.registerInputGetter(() => {
  return action.value ? { isValid: true, input: {} } : { isValid: false }
})

/** The deprecated review, still rendered with `flows_progress_sidebar` off. */
const summaryRows = computed<SummaryRow[] | undefined>(() => {
  return action.value?.meta?.review_summary?.rows.map((row) => ({
    ...row,
    editable:
      store.previousInputs.some((input) => input.resolver_id === row.resolver_id) ||
      (row.group_id != null && store.previousInputs.some((input) => input.resolver_id === row.group_id)),
  }))
})

const summaryGroups = computed<SummaryGroup[]>(() => {
  const reviewRows = summaryRows.value
  return reviewRows?.length ? groupConsecutiveReviewRows(reviewRows) : []
})

watchEffect(() => {
  if (!action.value) return

  if (showSelections.value) {
    if (rows.value.length === 0) {
      errorTracker.track('No selections summary rows found on confirm step', { action: action.value })
    }
    return
  }

  // TODO Should we also do this when rows is an empty array?
  if (!summaryRows.value) {
    errorTracker.track('No review summary rows found', { action: action.value })
  }
})

// TODO - Improve wording of user-facing error messaging - https://ticketure-team.atlassian.net/browse/TIC-5452
const errorMessage = computed<string | null>(() => {
  if (!action.value) return `Action is not a ${actionType} action`
  return null
})

function groupKey(group: SummaryGroup, index: number): string {
  const id = group.rows[0]?.group_id ?? group.rows[0]?.resolver_id ?? 'row'
  return `${id}-${index}`
}
</script>

<template>
  <div class="confirm-action">
    <template v-if="action">
      <ActionHeader
        :description="action.meta?.prompt"
        headingTag="h2"
        :icon="showSelections ? IconCheckupList : undefined"
        :title="action.meta?.summary_title"
      />

      <SelectionSummary
        v-if="showSelections"
        :rows="rows"
        :subtotal="subtotal"
        @edit="(resolverId, type) => emit('go-to-step', resolverId, type)"
      />

      <BaseCard v-else-if="summaryGroups.length">
        <TransitionGroup appear class="summary-content" name="transition-group-item" tag="div">
          <div
            v-for="(group, index) in summaryGroups"
            :key="groupKey(group, index)"
            class="summary-item"
            :style="{ '--index': index }"
          >
            <div class="summary-item-body">
              <div v-for="(row, rowIndex) in group.rows" :key="rowIndex" class="summary-item-row">
                <div v-if="row.heading" class="label">{{ row.heading }}</div>
                <div v-if="row.values?.length" class="value">
                  <div v-for="(paragraph, idx) in row.values" :key="idx" class="paragraph">
                    {{ paragraph }}
                  </div>
                </div>
              </div>
            </div>
            <BaseButton
              v-if="group.showEdit"
              hierarchy="secondary"
              :iconAfter="IconEdit"
              intent="neutral"
              shape="pill"
              size="xs"
              type="button"
              @click="emit('go-to-step', group.editTargetResolverId)"
            >
              Edit
            </BaseButton>
          </div>
        </TransitionGroup>
      </BaseCard>
    </template>

    <StatusMessage :icon="IconExclamationCircle" :message="errorMessage" variant="error" />
  </div>
</template>

<style scoped>
.summary-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-1-5);

  .summary-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-0-75);

    &:not(:last-child) {
      border-bottom: var(--divider);
      padding-bottom: var(--space-1-5);
    }

    .summary-item-body {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-0-5);
      width: 100%;

      .summary-item-row {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--space-0-25);
        width: 100%;

        .label {
          font-size: var(--size-md);
          font-weight: var(--font-weight-semibold);
          line-height: var(--line-height-heading);
          color: var(--text-primary);
        }

        .value {
          font-size: var(--size-sm);
          line-height: var(--line-height-body);
          color: var(--text-primary);
        }

        .paragraph {
          margin-bottom: var(--space-0-25);

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }
  }
}

.transition-group-item-enter-active {
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: calc(180ms * (var(--index, 0) / (var(--index, 0) + 2)));
}
.transition-group-item-enter-from {
  opacity: 0;
  transform: translateY(30px);
}
</style>
